/**
 * Actions Page - Manage actions, suggestions, and execution history
 */

import { useState } from 'react';
import { Zap, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ActionApprovalCard from '@/components/actions/ActionApprovalCard';
import {
  useActions,
  usePendingActionSuggestions,
  useActionExecutions
} from '@/hooks/useActions';

export default function Actions() {
  const [activeTab, setActiveTab] = useState('pending');
  const [resolvedIds, setResolvedIds] = useState(new Set());

  const { data: actions, isLoading: actionsLoading } = useActions();
  const { data: pending, isLoading: pendingLoading } = usePendingActionSuggestions();
  const { data: executions, isLoading: executionsLoading } = useActionExecutions({ limit: 20 });

  const displayPending = pending?.filter(s => !resolvedIds.has(s.id)) || [];

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Actions</h1>
        <p className="hidden sm:block text-sm text-muted-foreground">
          Manage AI-suggested actions, pre-approvals, and execution history
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'pending'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Pending Suggestions
          {pending && pending.length > 0 && (
            <Badge variant="primary" className="ml-2">
              {pending.length}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          All Actions
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'history'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Execution History
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {/* Pending Suggestions Tab */}
        {activeTab === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                Pending Action Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingLoading ? (
                <LoadingSpinner />
              ) : displayPending.length > 0 ? (
                <div className="space-y-3">
                  {displayPending.map((suggestion) => (
                    <ActionApprovalCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onResolved={(id) => setResolvedIds(prev => new Set([...prev, id]))}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
                  <p className="text-sm text-muted-foreground">
                    No pending action suggestions at the moment.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* All Actions Tab */}
        {activeTab === 'all' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Registered Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {actionsLoading ? (
                <LoadingSpinner />
              ) : actions && actions.length > 0 ? (
                <div className="space-y-3">
                  {actions.map((action) => (
                    <div
                      key={action.name}
                      className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium">{action.displayName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {action.description}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {!action.requiresApproval && (
                            <Badge variant="success">Auto-approved</Badge>
                          )}
                          {action.scope === 'none' && (
                            <Badge variant="secondary">No emails</Badge>
                          )}
                          {action.scope === 'single' && (
                            <Badge variant="secondary">Single email</Badge>
                          )}
                          {action.scope === 'multiple' && (
                            <Badge variant="secondary">Multiple emails</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>v{action.version}</span>
                        <span className="font-mono">{action.name}</span>
                        {!action.enabled && (
                          <Badge variant="warning">Disabled</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">No actions registered</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Execution History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Execution History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {executionsLoading ? (
                <LoadingSpinner />
              ) : executions && executions.length > 0 ? (
                <div className="space-y-3">
                  {executions.map((execution) => (
                    <div
                      key={execution.id}
                      className={`p-4 border rounded-lg ${
                        execution.success
                          ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {execution.success ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                            )}
                            <h4 className="font-medium">
                              {execution.action_name.replace(/_/g, ' ')}
                            </h4>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            {new Date(execution.executed_at).toLocaleString()}
                            {' · '}
                            {execution.execution_time_ms}ms
                            {' · '}
                            {execution.email_ids.length} email(s)
                          </p>
                          {execution.result && (
                            <div className="text-xs font-mono bg-white dark:bg-gray-900 p-2 rounded border mt-2">
                              {execution.success
                                ? execution.result.message || 'Success'
                                : execution.result.error || 'Failed'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    No execution history yet
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
